/** Runtime decoder of frozen B5369 schema, not a new generated/shared DTO.
 * Only normalized player fields leave this boundary; references/source never do.
 */
import { apiClient } from "@/shared/api/client";
import type { HouseholdService, LifeService, SessionService } from "@/shared/services/types";

export type BankIdentity = { userId: string; petId: string; householdId: string; homeId: string; credentialId: string };
export type BankScope = BankIdentity & { householdVersion: number };
export type BankView = {
  total: number; available: number; stay: number; savings: number | null; reserved: number;
  inflow: number; outflow: number; from: string; to: string; asOf: string;
  entries: Array<{ id: string; label: string; at: string; delta: number }>;
};
const fail = (): never => { throw Error("Bank facts are not confirmed for this scope"); };
function record(v: unknown): Record<string, unknown> {
  if (!v || typeof v !== "object" || Array.isArray(v)) return fail();
  return v as Record<string, unknown>;
}
function text(v: unknown): string { return typeof v === "string" && v.trim() ? v : fail(); }
function integer(v: unknown, signed = false): number {
  return typeof v === "number" && Number.isSafeInteger(v) && (signed || v >= 0) ? v : fail();
}
function instant(v: unknown): string {
  const s = text(v);
  return /(?:Z|[+-]\d\d:\d\d)$/.test(s) && Number.isFinite(Date.parse(s)) ? s : fail();
}
function sum(values: number[]): number { return integer(values.reduce((a, b) => a + b, 0)); }
const entryLabels: Readonly<Record<string, string>> = {
  item_sold: "物品售出", web_travel_fee: "旅途支出", web_farm_unlock: "菜地开垦",
  web_reward: "收到奖励", web_job_income: "打工收入", item_acquired: "物品购入",
};
export function decodeBankStatement(raw: unknown, scope: BankScope): BankView {
  const r = record(raw);
  if (r.pet_id !== scope.petId || r.household_id !== scope.householdId || r.currency_id !== "travel_coin"
      || r.unit_era !== "legacy" || r.minor_per_coin !== 1 || r.reconciliation_stage !== "LEGACY_OPENING_NOT_VERIFIED") return fail();
  const total = integer(r.total_minor), available = integer(r.available_minor), stay = integer(r.refundable_stay_held_minor), reserved = integer(r.combined_reserved_minor);
  const savings = r.savings_reserved_minor === null ? null : integer(r.savings_reserved_minor);
  if (r.savings_balance_minor !== savings || r.savings_stage !== (savings === null ? "NOT_INSTALLED" : "INSTALLED_WALLET_RESERVATIONS")
      || reserved !== sum([stay, savings ?? 0]) || total !== sum([available, reserved])) return fail();
  const from = instant(r.window_start), to = instant(r.window_end), asOf = instant(r.as_of);
  if (!(Date.parse(from) < Date.parse(to) && Date.parse(to) <= Date.parse(asOf))
      || Date.parse(to) - Date.parse(from) > 366 * 86400000) return fail();
  if (!Array.isArray(r.entries) || r.entries.length > 1000 || !Array.isArray(r.holds)) return fail();
  const ids = new Set<string>();
  const entries = r.entries.map(value => {
    const e = record(value), id = text(e.tx_id), kind = text(e.kind), at = instant(e.created_at), delta = integer(e.delta_minor, true);
    text(e.source); text(e.reference_id); // Required original schema fields, deliberately not returned.
    if (ids.has(id) || Date.parse(at) < Date.parse(from) || Date.parse(at) >= Date.parse(to)
        || e.classification !== (delta > 0 ? "cash_inflow" : delta < 0 ? "cash_outflow" : "non_cash")) return fail();
    ids.add(id);
    return { id, label: entryLabels[kind] ?? "账户记录", at, delta };
  }).sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  const held = r.holds.map(value => {
    const h = record(value), n = integer(h.amount_minor); text(h.journey_id);
    if (n === 0 || Date.parse(instant(h.reserved_at)) > Date.parse(asOf)) return fail();
    return n;
  });
  const inflow = integer(r.cash_inflow_minor), outflow = integer(r.cash_outflow_minor);
  if (sum(held) !== stay || sum(entries.filter(e => e.delta > 0).map(e => e.delta)) !== inflow
      || sum(entries.filter(e => e.delta < 0).map(e => -e.delta)) !== outflow) return fail();
  return { total, available, stay, savings, reserved, inflow, outflow, from, to, asOf, entries };
}
type BankServices = { session: SessionService; households: HouseholdService; life: LifeService };
export function bankScopeKey(s: BankScope): string { return JSON.stringify([s.userId, s.petId, s.householdId, s.homeId, s.credentialId, s.householdVersion]); }
export async function readCurrentBank(identity: BankIdentity, services: BankServices, signal: AbortSignal, current: () => boolean): Promise<{ scope: BankScope; data: BankView }> {
  const active = () => { if (signal.aborted || !current()) fail(); };
  const confirm = async (): Promise<BankScope> => {
    active();
    const [s, h] = await Promise.all([services.session.current(), services.households.detail(identity.householdId, signal)]);
    active();
    if (!s.authenticated || s.user?.user_id !== identity.userId || h.household.household_id !== identity.householdId
        || h.household.home_id !== identity.homeId || !h.household.home_activated
        || !h.household.pets.some(p => p.pet_id === identity.petId && p.join_step === "moved_in")
        || !h.members.some(m => m.user_id === identity.userId) || !Number.isSafeInteger(h.version) || h.version < 0) return fail();
    return { ...identity, householdVersion: h.version };
  };
  const scope = await confirm();
  // Current pet's original credential list must authorize this exact bank card;
  // old detail.balance or query cached membership is never a bank-read fallback.
  const [list, detail] = await Promise.all([services.life.credentials(identity.petId, signal), services.life.credential(identity.credentialId, signal)]);
  active();
  if (!list.some(c => c.credential_id === identity.credentialId && c.kind === "bank_card")
      || detail.summary.credential_id !== identity.credentialId || detail.summary.kind !== "bank_card") return fail();
  const raw = await apiClient.request<unknown>("/bank/statement", { method: "GET", query: { pet_id: identity.petId }, signal });
  active();
  const data = decodeBankStatement(raw, scope), after = await confirm();
  if (bankScopeKey(after) !== bankScopeKey(scope)) return fail();
  active(); return { scope, data };
}
